import { useEffect, useMemo, useState } from "react";
import DateBox from "./DateBox";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { Carousel } from "@mantine/carousel";
import { Center, ScrollArea, Space, Title } from "@mantine/core";

interface DatesCarouselProps {
  priceBedroom: number[];                         // index 0 = START
  availability: { Availabilities: number[] }[];   // dispo par catégorie
  selectedCure: any;
  dateSelect: any;
  handleDateSelected: (key: number, date: string, price: number) => void;
  allProduct: any[];
  nbNight: number;
  Petitdej: number;
  startStr?: string;   // "YYYY-MM-DD" inclus
  endExclStr?: string; // "YYYY-MM-DD" EXCLUS
}

const DateCarousel = ({
  priceBedroom = [],
  availability = [],
  selectedCure,
  dateSelect,
  handleDateSelected,
  allProduct = [],
  nbNight,
  Petitdej,
  startStr,
  endExclStr,
}: DatesCarouselProps) => {
  const [maxHeight, setMaxHeight] = useState(0);
  dayjs.locale("fr");

  // Référence START (aligne les index prix/dispo/affichage)
  const START = useMemo(
    () => (startStr ? dayjs(startStr).startOf("day") : dayjs().add(7, "day").startOf("day")),
    [startStr]
  );

  // Longueur exploitable dictée par les données
  const availabilityLengths = availability.map((a) => a?.Availabilities?.length ?? 0);
  const minAvailabilityLen = availabilityLengths.length ? Math.min(...availabilityLengths) : Infinity;
  const windowLengthFromData = Math.min(priceBedroom.length, minAvailabilityLen);

  // Fin exclusive dérivée des données
  const END_EXCL_DATA = useMemo(
    () => START.add(windowLengthFromData, "day"),
    [START, windowLengthFromData]
  );

  const END_EXCL_PROP = endExclStr ? dayjs(endExclStr).startOf("day") : null;
  const END_EXCL = useMemo(() => {
    if (!END_EXCL_PROP) return END_EXCL_DATA;
    return END_EXCL_PROP.isBefore(END_EXCL_DATA) ? END_EXCL_PROP : END_EXCL_DATA;
  }, [END_EXCL_PROP, END_EXCL_DATA]);

  const LATEST_ARRIVAL = useMemo(
    () => END_EXCL.subtract(nbNight, "day"),
    [END_EXCL, nbNight]
  );

  const allArrivalDates: string[] = useMemo(() => {
    const out: string[] = [];
    let d = START.clone();
    while (d.isBefore(LATEST_ARRIVAL, "day")) { // <-- change here (était !isAfter)
      out.push(d.format("YYYY-MM-DD"));
      d = d.add(1, "day");
    }
    return out;
  }, [START, LATEST_ARRIVAL]);

  const datesByMonth = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const dateStr of allArrivalDates) {
      const month = dayjs(dateStr).format("MMMM YYYY");
      if (!map[month]) map[month] = [];
      map[month].push(dateStr);
    }
    return map;
  }, [allArrivalDates]);

  // Index global (jours depuis START)
  const indexFromDate = (dateStr: string) => {
    const diff = dayjs(dateStr).diff(START, "day");
    return diff >= 0 ? diff : -1;
  };

  /**
   * Prix total = somme des prix chambre sur les nbNight nuits
   *            + (prix cure / nuit * nbNight)
   *            + (petit-déj / nuit * nbNight)
   * Si une nuit n'a pas de prix (0/undefined) => indispo => 0.
   */
  const findPriceForDate = (globalIndex: number): number => {
    if (globalIndex < 0) return 0;

    // tranche des prix chambre pour toutes les nuits
    const slice = priceBedroom.slice(globalIndex, globalIndex + nbNight);
    if (slice.length < nbNight) return 0;
    if (slice.some((p) => !p || p <= 0)) return 0;

    // prix cure par nuit
    const priceCureObj =
      selectedCure?.Mews_id &&
      allProduct.find((product: any) => product.Id === selectedCure.Mews_id);
    const priceCurePerNight = Number(priceCureObj?.Pricing?.Value?.GrossValue ?? 0);

    // total
    const roomSum = slice.reduce((acc, p) => acc + Number(p), 0);
    const total =
      roomSum +
      priceCurePerNight * nbNight +
      Number(Petitdej ?? 0) * nbNight;

    return Math.round(total);
  };

  /**
   * Indispo si :
   *  (1) pas assez de jours restants pour nbNight,
   *  (2) au moins une des nbNight nuits est full (toutes catégories 0).
   */
  const isFullForArrival = (globalIndex: number): boolean => {
    if (globalIndex < 0) return true;

    // il faut nbNight nuits: index + nbNight <= fenêtre data
    if (globalIndex + nbNight > windowLengthFromData) return true;

    // check "full" sur chaque nuit i = 0 .. nbNight-1
    if (selectedCure?.Hotel?.id !== 1) {
      for (let i = 0; i < nbNight; i++) {
        const idx = globalIndex + i;
        const fullThisNight = availability.every(
          (avai) => (avai?.Availabilities?.[idx] ?? 0) === 0
        );
        if (fullThisNight) return true;
      }
    }
    return false;
  };

  // Hauteur scroll responsive
  useEffect(() => {
    const updateMaxHeight = () => {
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      setMaxHeight(windowHeight);
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, []);

  // Slides
  const slides = useMemo(() => {
    return Object.keys(datesByMonth).map((month, idx) => {
      const dates = datesByMonth[month];

      const boxes = dates.map((dateStr) => {
        const globalIndex = indexFromDate(dateStr);
        const totalPrice = findPriceForDate(globalIndex);
        const full = isFullForArrival(globalIndex);

        const priceForBox: number | "Non disponible" =
          full || totalPrice === 0 ? "Non disponible" : totalPrice;

        return (
          <DateBox
            key={globalIndex}
            index={globalIndex}
            date={dateStr}
            dateSelect={dateSelect}
            handleDateSelected={handleDateSelected}
            price={priceForBox}
          />
        );
      });

      return (
        <Carousel.Slide key={idx}>
          <Center>
            <Title className="card-date-title" order={6}>
              {nbNight === 1 ? (
                <>
                  {month} - {nbNight} nuit
                </>
              ) : (
                <>
                  {month} - {nbNight} nuits
                </>
              )}
            </Title>
          </Center>
          <Space h="xl" />
          <ScrollArea.Autosize maxHeight={maxHeight * 0.9 - 400} mx="auto">
            {boxes}
          </ScrollArea.Autosize>
        </Carousel.Slide>
      );
    });
  }, [
    datesByMonth,
    nbNight,
    dateSelect,
    availability,
    priceBedroom,
    Petitdej,
    allProduct,
    selectedCure,
    windowLengthFromData,
  ]);

  return <Carousel>{slides}</Carousel>;
};

export default DateCarousel;
